<template>
  <!-- eslint-disable max-len  -->

  <div v-if="emailSend">
    <div style="margin-bottom: 0.5rem">
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M20 0C31.0723 0 40 8.97756 40 20C40 31.0723 31.0723 40 20 40C8.97756 40 0 31.0723 0 20C0 8.97756 8.97756 0 20 0ZM14.8628 25.5362L8.57855 19.2519C8.52868 19.202 8.4788 19.202 8.42893 19.2519L6.48379 21.197C6.43392 21.2469 6.43392 21.3466 6.48379 21.3965L15.212 30.1746C15.2618 30.2244 15.3616 30.2244 15.4115 30.1746L33.5661 12.02C33.616 11.9701 33.616 11.8703 33.5661 11.8204L31.6209 9.87531C31.5711 9.82544 31.4713 9.82544 31.4214 9.87531L15.8105 25.5362C15.5611 25.7855 15.1122 25.7855 14.8628 25.5362ZM20 1.19701C9.62594 1.19701 1.19701 9.62594 1.19701 20C1.19701 30.4239 9.62594 38.8529 20 38.8529C30.4239 38.8529 38.8529 30.4239 38.8529 20C38.8529 9.62594 30.4239 1.19701 20 1.19701Z"
          fill="#27AE60"
        />
      </svg>
    </div>
    {{ emailSend }}
  </div>
  <div v-else>
    <div class="form-panel">
      <div v-if="!displayRecoveryEmail">
        <PanelHeader
          :displayBack="displayBack"
          :titleText="$t(LABEL_PASSWORD_RESET)"
          :helpText="$t(LABEL_PASSWORD_RESET_HELP, [this.serviceName])"
          :helpTextLeft="true"
          @onBack="$emit('back')"
        />
        <div class="form-group">
          <div class="form-label">{{ $t(LABEL_USERNAME_OR_PRIMARY_EMAIL) }}</div>
          <input
            type="text"
            v-bind:class="{ 'is-danger': isEmailInvalid }"
            class="form-control"
            v-model.trim="email"
            tabindex="0"
            autocomplete="off"
            spellcheck="false"
            autocapitalize="none"
            ref="emailField"
            @keyup="onEmailKeyUp"
            :disabled="processing"
            v-on:keyup.enter.prevent="sendCode"
          />
          <div class="help" v-if="emailLabel" v-bind:class="{ 'is-danger': isEmailInvalid }" v-html="emailLabel"></div>
        </div>

        <div class="btn-panel">
          <a
            class="btn btn-full"
            tabindex="0"
            v-bind:class="{ 'is-disabled': processing, 'is-loading': processing }"
            @keyup.enter="sendCode"
            @click="sendCode"
            >{{ $t(LABEL_CONTINUE) }}</a
          >
        </div>
      </div>
      <div v-else>
        <PanelHeader
          :titleText="$t(LABEL_PASSWORD_RESET)"
          :helpText="$t(LABEL_PASSWORD_RESET_RECOVERY_EMAIL_HELP)"
          :helpTextLeft="true"
          :displayBack="true"
          v-on:onBack="onBack"
        />
        <div class="form-group">
          <div class="form-label">{{ $t(LABEL_RECOVERY_EMAIL) }}</div>
          <input
            type="email"
            v-bind:class="{ 'is-danger': isRecoveryEmailInvalid }"
            class="form-control"
            v-model.trim="recoveryEmail"
            tabindex="0"
            autocomplete="off"
            spellcheck="false"
            autocapitalize="none"
            ref="recoveryField"
            @keyup="onRecoveryKeyUp"
            :disabled="processing"
            v-on:keyup.enter.prevent="sendCode"
          />
          <div class="help" v-if="recoveryLabel" v-bind:class="{ 'is-danger': isRecoveryEmailInvalid }" v-html="recoveryLabel"></div>
        </div>

        <div class="btn-panel">
          <a
            class="btn btn-full"
            tabindex="0"
            v-bind:class="{ 'is-disabled': processing, 'is-loading': processing }"
            @keyup.enter="sendCode"
            @click="sendCode"
            >{{ $t(LABEL_PASSWORD_RESET) }}</a
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */

import { defineComponent, nextTick } from 'vue';
import PanelHeader from '@/components/panel-header.vue';
import focus from '@/libs/focus';
import isEmail from 'validator/lib/isEmail';
import {
  LABEL_CONTINUE,
  LABEL_INVALID_EMAIL,
  LABEL_PASSWORD_RESET,
  LABEL_PASSWORD_RESET_HELP,
  LABEL_PASSWORD_RESET_RECOVERY_EMAIL_HELP,
  LABEL_USERNAME_OR_PRIMARY_EMAIL,
  LABEL_RECOVERY_EMAIL,
} from '@/libs/i18nLabels';
import passwordState from './passwordState';

export default defineComponent({
  components: {
    PanelHeader,
  },

  props: {
    serviceName: String,
    displayBack: Boolean,
  },

  emits: ['back'],
  setup() {
    return {
      LABEL_CONTINUE,
      LABEL_INVALID_EMAIL,
      LABEL_PASSWORD_RESET,
      LABEL_PASSWORD_RESET_HELP,
      LABEL_PASSWORD_RESET_RECOVERY_EMAIL_HELP,
      LABEL_RECOVERY_EMAIL,
      LABEL_USERNAME_OR_PRIMARY_EMAIL,
    };
  },
  data() {
    return {
      email: '',
      processing: false,
      isEmailInvalid: false,
      emailLabel: '',

      recoveryEmail: '',
      displayRecoveryEmail: false,
      isRecoveryEmailInvalid: false,
      recoveryLabel: '',

      emailSend: '',
    };
  },

  methods: {
    onEmailKeyUp() {
      if (!this.isEmailInvalid) {
        return;
      }
      this.isEmailInvalid = false;
      this.emailLabel = '';
    },

    onRecoveryKeyUp() {
      if (!this.isRecoveryEmailInvalid) {
        return;
      }
      this.isRecoveryEmailInvalid = false;
      this.recoveryLabel = '';
    },

    focusEmailField() {
      focus(this.$refs.emailField);
    },

    focusRecoveryField() {
      focus(this.$refs.recoveryField);
    },

    onBack() {
      // cleanup
      this.isRecoveryEmailInvalid = false;
      this.recoveryLabel = '';
      this.recoveryEmail = '';
      this.displayRecoveryEmail = false;
    },

    async sendCode() {
      if (this.email.length < 2) {
        this.focusEmailField();
        return;
      }

      if (this.email.indexOf('@') >= 0 && !isEmail(this.email)) {
        this.isEmailInvalid = true;
        this.emailLabel = this.$t(LABEL_INVALID_EMAIL);
        this.focusEmailField();
        return;
      }

      if (this.displayRecoveryEmail) {
        // recovery email validation
        if (this.recoveryEmail.trim().length < 2) {
          this.focusRecoveryField();
          return;
        }

        if (!isEmail(this.recoveryEmail)) {
          this.isRecoveryEmailInvalid = true;
          this.recoveryLabel = this.$t(LABEL_INVALID_EMAIL);
          this.focusRecoveryField();
          return;
        }
      }

      this.processing = true;

      this.isEmailInvalid = false;
      this.emailLabel = '';
      const res = await passwordState.sendResetLink(
        { username: this.email, email: this.displayRecoveryEmail ? this.recoveryEmail : undefined },
        this.$i18n,
      );

      const hasError = !res || res.errorCode;
      const errorMessage = res && res.errorMessage;

      if (!this.displayRecoveryEmail) {
        // firstpanel
        if (hasError) {
          if (errorMessage) {
            this.isEmailInvalid = true;
            this.emailLabel = errorMessage || '';
          }

          this.focusEmailField();
        } else {
          // display recovery panel
          this.displayRecoveryEmail = true;

          nextTick(() => {
            this.focusRecoveryField();
          });
        }
      } else if (this.displayRecoveryEmail) {
        // second panel
        if (hasError) {
          if (errorMessage) {
            this.isRecoveryEmailInvalid = true;
            this.recoveryLabel = errorMessage || '';
          }
          this.focusRecoveryField();
        } else {
          // success
          this.emailSend = res?.successMessage || '?';
        }
      }

      this.processing = false;
    },
  },

  mounted() {
    focus(this.$refs.emailField);
  },
});
</script>
